import { Box, Button, Divider, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Query } from 'src/components/atoms/query';

import { CreateDestinationFormValues } from '../../organisms/create-destination/create-destination-form';

import CautionCard from '@/components/atoms/caution-card';
import { ListCard } from '@/components/atoms/list-card';
import StepTitle from '@/components/atoms/step-title';
import { DataStatusIndicator } from '@/components/molecules/data-status-indicator/data-status-indicator';
import { CompactPagination } from '@/components/organisms/compact-pagination';
import {
  CreateDestinationContext,
  CreateDestinationEvent,
} from '@/features/destinations/utils/machine/create-destination-machine';
import { useHostsWithSearch } from '@/features/hosts/hooks/query/use-hosts-with-search';
import { usePagination } from '@/hooks/use-pagination';
import { getLabel } from '@/utils/get-label';

interface Props {
  context: CreateDestinationContext;
  send: (event: CreateDestinationEvent) => void;
}

export const Hosts = ({ context, send }: Props) => {
  const { t } = useTranslation(['common', 'createDestination']);

  const { control, watch, setValue } = useFormContext<CreateDestinationFormValues>();

  const hosts = watch('hosts') || [];

  const {
    changePage,
    changePerPage,
    page,
    perPage,
    setTotalItems,
    totalPages,
    nextDisabled,
    nextPage,
    prevDisabled,
    prevPage,
  } = usePagination();

  const [query, setQuery] = useState('');

  const { data, isLoading, isFetching, error } = useHostsWithSearch({
    page,
    perPage,
    email: query,
  });

  const handleChangeQuery = (value: string) => {
    setQuery(value);
    changePage(1);
  };

  useEffect(() => {
    if (data) {
      setTotalItems(data.total);
    }
  }, [data]);

  const handleToggleHostSelection = ({
    id,
    firstName,
    lastName,
    email,
  }: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  }) => {
    const currentHosts = [...hosts];

    const existingHostIndex = currentHosts.findIndex(host => host.id === id);

    if (existingHostIndex !== -1) {
      const updatedHosts = currentHosts.filter(host => host.id !== id);
      setValue('hosts', updatedHosts);
    } else {
      const name = getLabel({
        values: [firstName, lastName],
        fallback: email || '-',
      });

      const newHost = {
        id,
        name,
      };
      setValue('hosts', [...currentHosts, newHost]);
    }
  };

  return (
    <div>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridAutoFlow: 'row',
        }}>
        <StepTitle
          title={t('common:stepTitle', {
            step: context.step,
            title: t(context.content),
          })}
        />

        <CautionCard
          title={t('common:optional')}
          description={t('createDestination:optionalStepDescription')}
          sx={{ mb: 2 }}
        />

        <Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('common:search')}
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Query
              searchAsType={true}
              disabled={false}
              onChange={newQuery => handleChangeQuery(newQuery)}
              value={query}
            />
          </Box>

          <Controller
            control={control}
            name="hosts"
            render={({ field: { value: _value } }) => {
              return (
                <Box sx={{ my: 2 }}>
                  {data?.resources?.map(({ id, firstName, lastName, email, phoneNumber }) => {
                    const isSelected = hosts.some(host => host.id === id);

                    return (
                      <ListCard
                        key={id}
                        name={firstName}
                        secondaryName={lastName}
                        description={email}
                        rightText={phoneNumber}
                        isSelected={isSelected}
                        onClick={() => {
                          handleToggleHostSelection({ id, firstName, lastName, email });
                        }}
                      />
                    );
                  })}
                  <DataStatusIndicator
                    dataLength={data?.resources?.length ?? 0}
                    isFetching={isLoading}
                    error={error?.response?.data?.message}
                  />
                </Box>
              );
            }}
          />
          <Divider sx={{ mt: 'auto' }} />
          <CompactPagination
            page={page}
            perPage={perPage}
            totalPages={totalPages}
            changePage={changePage}
            disabled={isLoading}
            handlePerPageChange={changePerPage}
            nextDisabled={nextDisabled}
            isFetching={isFetching}
            nextPage={nextPage}
            prevDisabled={prevDisabled}
            prevPage={prevPage}
          />
        </Box>

        <div>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mt: 3,
            }}>
            <Button
              color="primary"
              onClick={() => send({ type: 'BACK' })}
              size="large"
              sx={{ mr: 2 }}
              type="button"
              variant="text">
              {t('common:button.back')}
            </Button>
            <Button
              color="primary"
              size="large"
              type="button"
              variant="contained"
              onClick={() => send({ type: 'NEXT' })}>
              {t('common:button.nextStep')}
            </Button>
          </Box>
        </div>
      </Box>
    </div>
  );
};
